/**
 * Read-only transcript of one voice-agent call (Avessa webhook), opened from
 * the /voice-agent page. Fetched only while open — transcripts never travel
 * in list payloads. Birth numbers are already redacted ([RČ]) server-side.
 */

import type { AppRouter } from '@dentaly/server/trpc'
import type { inferRouterOutputs } from '@trpc/server'
import { Copy, FileText, PhoneIncoming, PhoneOutgoing } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { formatDateCzech } from '@/lib/calendar-utils'
import { trpc } from '@/trpc/client'

type Transcript =
	inferRouterOutputs<AppRouter>['voiceAgent']['calls']['transcript']
type Turn = Transcript['turns'][number]

const ROLE_LABELS: Record<Turn['role'], string> = {
	agent: 'Asistentka',
	caller: 'Volající',
}

export function formatDuration(sec: number | null): string | null {
	if (sec == null) return null
	return `${Math.floor(sec / 60)}:${(sec % 60).toString().padStart(2, '0')}`
}

function formatClock(date: Date, withSeconds = false): string {
	return date.toLocaleTimeString('cs-CZ', {
		hour: '2-digit',
		minute: '2-digit',
		...(withSeconds ? { second: '2-digit' } : {}),
		timeZone: 'Europe/Prague',
	})
}

/** Turn time as sent: ISO timestamp → clock, number → offset from call start. */
function formatTurnTime(at: Turn['at']): string | null {
	if (at == null) return null
	if (typeof at === 'number') return formatDuration(Math.round(at))
	const date = new Date(at)
	return Number.isNaN(date.getTime()) ? at : formatClock(date, true)
}

/** Codes a retry can't fix: no transcript, or not signed in. */
const FINAL_ERROR_CODES = new Set(['NOT_FOUND', 'UNAUTHORIZED', 'FORBIDDEN'])

function errorMessage(code: string | undefined): string {
	if (code === 'NOT_FOUND') return 'Přepis tohoto hovoru není k dispozici.'
	if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN') {
		return 'K přepisu nemáte přístup – přihlaste se znovu.'
	}
	if (code === 'INTERNAL_SERVER_ERROR') {
		return 'Přepis se nepodařilo načíst (chyba serveru).'
	}
	return 'Přepis se nepodařilo načíst. Zkontrolujte připojení.'
}

function transcriptAsText(turns: Turn[]): string {
	return turns.map((t) => `${ROLE_LABELS[t.role]}: ${t.text}`).join('\n')
}

export function CallTranscriptDialog({
	voiceCallId,
	onOpenChange,
}: {
	/** null = closed */
	voiceCallId: string | null
	onOpenChange: (open: boolean) => void
}) {
	const { data, isLoading, isError, isFetching, error, refetch } =
		trpc.voiceAgent.calls.transcript.useQuery(
			{ voiceCallId: voiceCallId ?? '' },
			{
				enabled: voiceCallId != null,
				staleTime: Number.POSITIVE_INFINITY,
				retry: (count, err) =>
					!FINAL_ERROR_CODES.has(err.data?.code ?? '') && count < 1,
			},
		)
	const errorCode = error?.data?.code

	const copy = async () => {
		if (!data) return
		try {
			await navigator.clipboard.writeText(transcriptAsText(data.turns))
			toast.success('Přepis zkopírován')
		} catch {
			toast.error('Přepis se nepodařilo zkopírovat')
		}
	}

	const call = data?.call
	const DirectionIcon =
		call?.direction === 'outbound' ? PhoneOutgoing : PhoneIncoming

	return (
		<Dialog open={voiceCallId != null} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<FileText className="h-5 w-5 text-[#d7ad6c]" />
						Přepis hovoru
					</DialogTitle>
					<DialogDescription asChild>
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#8c8275]">
							{call ? (
								<>
									<span className="flex items-center gap-1.5">
										<DirectionIcon className="h-4 w-4 text-[#d7ad6c]" />
										{call.direction === 'outbound' ? 'Odchozí' : 'Příchozí'}
									</span>
									<span>
										{formatDateCzech(new Date(call.startedAt), 'd. M. yyyy')}{' '}
										{formatClock(new Date(call.startedAt))}
									</span>
									{formatDuration(call.durationSec) && (
										<span>Délka {formatDuration(call.durationSec)}</span>
									)}
									{call.phone && (
										<span>
											{call.phone.replace(
												/^(\d{3})(\d{3})(\d{3})$/,
												'$1 $2 $3',
											)}
										</span>
									)}
									{call.outcome && <span>Výsledek: {call.outcome}</span>}
								</>
							) : (
								<span>Hlasový agent</span>
							)}
						</div>
					</DialogDescription>
				</DialogHeader>

				<div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
					{isLoading && <p className="text-[#8c8275]">Načítám přepis…</p>}
					{isError && (
						<div className="space-y-3">
							<p>{errorMessage(errorCode)}</p>
							{!FINAL_ERROR_CODES.has(errorCode ?? '') && (
								<Button
									variant="outline"
									size="sm"
									disabled={isFetching}
									onClick={() => refetch()}
								>
									{isFetching ? 'Načítám…' : 'Zkusit znovu'}
								</Button>
							)}
						</div>
					)}
					{data?.turns.map((turn, i) => {
						const time = formatTurnTime(turn.at)
						const agent = turn.role === 'agent'
						return (
							<div
								// biome-ignore lint/suspicious/noArrayIndexKey: turns are static and ordered
								key={i}
								className={`flex ${agent ? 'justify-start' : 'justify-end'}`}
							>
								<div
									className={`max-w-[85%] rounded-xl px-4 py-2.5 ${
										agent
											? 'bg-[#f5ebe0] text-[#3d3428]'
											: 'border border-[#e8e4df] bg-white text-[#242424]'
									}`}
								>
									<p className="mb-0.5 text-[11px] uppercase tracking-[1px] text-[#8c8275]">
										{ROLE_LABELS[turn.role]}
										{time && ` · ${time}`}
									</p>
									<p className="whitespace-pre-wrap break-words">{turn.text}</p>
								</div>
							</div>
						)
					})}
				</div>

				<DialogFooter>
					<Button variant="outline" disabled={!data} onClick={copy}>
						<Copy className="mr-2 h-4 w-4" />
						Kopírovat
					</Button>
					<Button onClick={() => onOpenChange(false)}>Zavřít</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
